import {
  type AsyncBuffer,
  type FileMetaData,
  type ParquetParsers,
  parquetMetadataAsync,
  parquetSchema,
} from "hyparquet";
import { DEFAULT_PARSERS } from "hyparquet/src/convert.js";
import { parquetPlan } from "hyparquet/src/plan.js";
import { assembleAsync, readRowGroup } from "hyparquet/src/rowgroup.js";
import type { ParquetReadOptions } from "hyparquet/src/types.js";
import { compressors } from "hyparquet-compressors";

import { type Readable } from "@/shared/fs";

import { Channel } from "./Channel";
import { matchesFilter } from "./filter";
import type {
  ColumnData,
  IterColumnChunksOptions,
  IterRowGroupsOptions,
  ReaderLike,
  RowGroup,
} from "./ReaderLike";

/**
 * The parsers a reader decodes with unless told otherwise: hyparquet's
 * defaults, except that temporal values stay integers instead of becoming
 * `Date`s: a timestamp is a bigint count of its unit, and a date is a number
 * of days.
 */
export const READER_PARSERS: ParquetParsers = {
  ...DEFAULT_PARSERS,
  timestampFromMilliseconds(millis: bigint) {
    return millis;
  },
  timestampFromMicroseconds(micros: bigint) {
    return micros;
  },
  timestampFromNanoseconds(nanos: bigint) {
    return nanos;
  },
  dateFromDays(days: number) {
    return days;
  },
};

/**
 * Reads every BYTE_ARRAY leaf of a column as the bytes it is stored as, where
 * {@link READER_PARSERS} would decode an unannotated one as UTF-8 text. For a
 * binary column — a packed point cloud, a compressed image — the text decode
 * is lossy: any byte that is not valid UTF-8 becomes U+FFFD. Applied per
 * column, by a topic reader asked for that column's bytes.
 */
export const BINARY_PARSERS: Partial<ParquetParsers> = {
  stringFromBytes: (bytes: Uint8Array) => bytes,
};

export interface ParquetReaderOptions {
  file: Readable;
  columns: string[];
  metadata: FileMetaData;
  byteLength: number;
  offset?: number;
  /** Replaces the reader's parsers of the same names. */
  parsers?: Partial<ParquetParsers>;
  /**
   * Whether an unannotated BYTE_ARRAY decodes as UTF-8 text, hyparquet's
   * default, or stays the bytes it is stored as. A column annotated as a
   * STRING decodes as text either way.
   */
  utf8?: boolean;
}

interface FactoryArgs {
  file: Readable;
  columns?: string[];
  metadata?: FileMetaData;
  offset?: number;
  parsers?: Partial<ParquetParsers>;
  /** See {@link ParquetReaderOptions.utf8}. */
  utf8?: boolean;
}

export class ParquetReader implements ReaderLike {
  static async forFile(args: FactoryArgs): Promise<ParquetReader> {
    const { file, offset, parsers, utf8 } = args;
    const byteLength = Number(await file.size());

    let metadata = args.metadata;
    if (metadata === undefined) {
      metadata = await parquetMetadataAsync({
        byteLength,
        async slice(start: number, end: number) {
          const view = await file.read(start, end - start);
          return view.buffer as ArrayBuffer;
        },
      });
    }

    let columns = args.columns;
    if (columns === undefined) {
      // first element in schema list is the root
      columns = metadata.schema.map((col) => col.name).slice(1);
    }

    return new ParquetReader({
      file,
      columns,
      metadata,
      byteLength,
      offset,
      parsers,
      utf8,
    });
  }

  private readonly byteLength: number;
  public readonly columns: string[];
  private readonly file: Readable;
  public readonly metadata: FileMetaData;
  /*
   * For a cached part, the source file's row index of the part's first row;
   * 0 for any other file. It is added to each row group's and chunk's row
   * bounds before they are matched against `rowGroupFilter` and
   * `rowIndexFilter`, which count rows of the source file. Yielded row groups
   * and chunks keep this file's own row indices.
   */
  private readonly offset: number;
  private readonly parsers: ParquetParsers;
  private readonly utf8: boolean | undefined;

  constructor(options: ParquetReaderOptions) {
    this.file = options.file;
    this.columns = options.columns;
    this.metadata = options.metadata;
    this.byteLength = options.byteLength;
    this.offset = options.offset ?? 0;
    this.parsers = { ...READER_PARSERS, ...options.parsers };
    this.utf8 = options.utf8;
  }

  get rowCount(): bigint {
    return this.metadata.num_rows;
  }

  dispose(): void {
    /** noop */
  }

  /**
   * Async generator that yields column chunks as they become available.
   *
   * Processes row groups sequentially, but reads all columns within each row group
   * concurrently. Uses a Channel (async queue) to yield chunks immediately as column
   * data arrives, rather than waiting for all columns to complete.
   *
   * Error handling: If any column read fails, an internal AbortController cancels all
   * other concurrent reads. Errors are surfaced after the channel is drained.
   *
   * Summary of concurrency model:
   *
   *    ┌─────────────────────────────────────────────────────────────┐
   *    │                    For each Row Group                       │
   *    │  ┌─────────────────────────────────────────────────────┐    │
   *    │  │  PRODUCERS (parallel)          │    CONSUMER        │    │
   *    │  │  ┌──────────┐                  │                    │    │
   *    │  │  │ Column 1 │──┐               │                    │    │
   *    │  │  └──────────┘  │               │                    │    │
   *    │  │  ┌──────────┐  ├──► Channel ───┼──► yield* ────────►│    │
   *    │  │  │ Column 2 │──┤               │                    │    │
   *    │  │  └──────────┘  │               │                    │    │
   *    │  │  ┌──────────┐  │               │                    │    │
   *    │  │  │ Column N │──┘               │                    │    │
   *    │  │  └──────────┘                  │                    │    │
   *    │  └─────────────────────────────────────────────────────┘    │
   *    └─────────────────────────────────────────────────────────────┘
   *
   * Goal: maximum I/O parallelism (all columns read concurrently) while providing streaming output
   * (chunks yielded as soon as available, not waiting for all columns to complete).
   */
  async *iterColumnChunks(
    options?: Partial<IterColumnChunksOptions>,
  ): AsyncGenerator<ColumnData, void, void> {
    const {
      columns = this.columns,
      rowGroupFilter,
      rowIndexFilter,
      signal,
    } = options ?? {};
    for await (const rowGroup of this.iterRowGroups({
      rowGroupFilter,
      rowIndexFilter,
    })) {
      signal?.throwIfAborted();

      const channel = new Channel<ColumnData>();

      // Mechanism to abort concurrent/queued read operations if one fails
      const errorController = new AbortController();
      const combinedSignal =
        signal !== undefined
          ? AbortSignal.any([signal, errorController.signal])
          : errorController.signal;

      const file = this.getAsyncBuffer(combinedSignal);
      const readOptions: ParquetReadOptions = {
        file,
        compressors,
        parsers: this.parsers,
        utf8: this.utf8,
      };
      const queryPlan = parquetPlan({
        file,
        metadata: this.metadata,
        rowStart: rowGroup.rowStart,
        rowEnd: rowGroup.rowEnd,
        columns,
      });

      if (queryPlan.groups.length === 0) {
        continue;
      }

      const groupPlan = queryPlan.groups[0];

      const unassembled = readRowGroup(readOptions, queryPlan, groupPlan);
      const schema = parquetSchema(this.metadata);
      const assembled = assembleAsync(unassembled, schema, this.parsers);

      // kick off data read, do not await
      const read = Promise.allSettled(
        assembled.asyncColumns.map((column) => {
          // queue chunks for yielding as available
          return column.data
            .then(({ data, skipped }) => {
              combinedSignal?.throwIfAborted();

              let rowStart = unassembled.groupStart + skipped;
              for (const columnData of data) {
                // chunk contains values from a single data page for a single column within a row group
                // a column's worth of data for a row group may be split across many data pages
                // https://parquet.apache.org/docs/file-format/
                const chunk: ColumnData = {
                  columnName: column.pathInSchema[0],
                  columnData: columnData as ColumnData["columnData"],
                  rowStart,
                  rowEnd: rowStart + columnData.length,
                };

                const adjusted = this.adjustBoundsByOffset(chunk);
                if (
                  rowIndexFilter === undefined ||
                  matchesFilter(adjusted, rowIndexFilter)
                ) {
                  channel.push(chunk);
                }
                rowStart += columnData.length;
              }
            })
            .catch((reason) => {
              errorController.abort();
              throw reason;
            });
        }),
      ).finally(() => channel.close());

      // yield chunks as available
      yield* channel;

      // await all chunks, surface first error if any
      const results = await read;
      const rejection = results.find((r) => r.status === "rejected");
      if (rejection !== undefined) {
        throw rejection.reason;
      }
    }
  }

  async *iterRowGroups(
    params?: Partial<IterRowGroupsOptions>,
  ): AsyncIterable<RowGroup> {
    const { rowGroupFilter, rowIndexFilter } = params ?? {};
    let rowStart = 0; // first row index of the current group
    for (const parquetRowGroup of this.metadata.row_groups) {
      const rowCount = Number(parquetRowGroup.num_rows);
      const rowEnd = rowStart + rowCount;
      const rowGroup = { ...parquetRowGroup, rowStart, rowEnd };
      const adjusted = this.adjustBoundsByOffset(rowGroup);
      if (
        rowCount > 0 &&
        (rowGroupFilter === undefined || rowGroupFilter(adjusted)) &&
        (rowIndexFilter === undefined ||
          matchesFilter(adjusted, rowIndexFilter))
      ) {
        yield Promise.resolve(rowGroup);
      }

      rowStart += rowCount;
    }
  }

  private adjustBoundsByOffset<T>(
    bounds: T & { rowStart: number; rowEnd: number },
  ): T {
    return {
      ...bounds,
      rowStart: bounds.rowStart + this.offset,
      rowEnd: bounds.rowEnd + this.offset,
    };
  }

  private getAsyncBuffer(signal?: AbortSignal): AsyncBuffer {
    return {
      byteLength: this.byteLength,
      slice: async (start: number, end: number) => {
        const view = await this.file.read(start, end - start, signal);
        return view.buffer as ArrayBuffer;
      },
    };
  }
}
